// @file Vuex store module for all things related to contributing status indicator for a surface
import Vue from 'vue'
import { User } from '@padlet/arvo'
import { Id } from 'types'
import PadletApi from 'surface/padlet_api'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'
type UserId = Id
interface StatusObject {
  user: User
  status: ContributingStatus
}
enum ContributingStatus {
  INACTIVE = 0,
  ACTIVE = 1,
}

interface ContributingStatusState {
  sentStatus: ContributingStatus
  statusEntities: Record<UserId, StatusObject>
  statusExpireTimeout: Record<UserId, number>
  statusRefreshTimeout: number | undefined
  orderedActiveUsers: number[]
}

const SHOWN_ACTIVE_CONTRIBUTORS_COUNT_FALLBACK = 3

const blankState = (): ContributingStatusState => ({
  sentStatus: ContributingStatus.INACTIVE,
  statusEntities: {},
  statusExpireTimeout: {},
  statusRefreshTimeout: undefined,
  orderedActiveUsers: [],
})

const initialState = (): ContributingStatusState => ({
  sentStatus: ContributingStatus.INACTIVE,
  statusEntities: {},
  statusExpireTimeout: {},
  statusRefreshTimeout: undefined,
  orderedActiveUsers: [],
})

const contributingStatusModule: Module<ContributingStatusState, RootState> = {
  namespaced: true,
  state: blankState,
  getters: {
    xActivityIndicator: (_, getters): boolean => getters.numberOfActiveContributors > 0,
    xAdditionalContributors: (_, getters): boolean => getters.numberOfAdditionalContributors > 1,
    numberOfActiveContributors: (state: ContributingStatusState): number => state.orderedActiveUsers.length,
    numberOfAdditionalContributors: (_, getters): number =>
      getters.numberOfActiveContributors - SHOWN_ACTIVE_CONTRIBUTORS_COUNT_FALLBACK,
    activeContributorsList: (state: ContributingStatusState, getters): User[] => {
      // Until 4 contributors we always show avatars.
      // If there are more than 4, we only show SHOWN_ACTIVE_CONTRIBUTORS_COUNT_FALLBACK.
      const numberOfShownActiveContributors: number = getters.xAdditionalContributors
        ? SHOWN_ACTIVE_CONTRIBUTORS_COUNT_FALLBACK
        : getters.numberOfActiveContributors
      return state.orderedActiveUsers
        .map((x) => state.statusEntities[x].user)
        .filter((x) => !!x)
        .slice(0, numberOfShownActiveContributors)
    },
  },
  mutations: {
    SET_ACTIVE_STATUS(state: ContributingStatusState, { user }): void {
      const userId: number = user.id
      Vue.set(state.statusEntities, userId, { status: ContributingStatus.ACTIVE, user })
      if (!state.orderedActiveUsers.includes(userId)) {
        state.orderedActiveUsers = state.orderedActiveUsers.concat([userId])
      }
    },
    CLEAR_ACTIVE_STATUS(state: ContributingStatusState, { user }): void {
      const userId = user.id
      Vue.set(state.statusEntities, userId, { status: ContributingStatus.INACTIVE, user })
      state.orderedActiveUsers = state.orderedActiveUsers.filter((x) => x !== userId)
    },
    SET_EXPIRE_TIMEOUT(state: ContributingStatusState, { user, timeout }): void {
      const userId = user.id
      Vue.set(state.statusExpireTimeout, userId, timeout)
    },
    CLEAR_EXPIRE_TIMEOUT(state: ContributingStatusState, { user }): void {
      const userId = user.id
      Vue.delete(state.statusExpireTimeout, userId)
    },
    SET_REFRESH_TIMEOUT(state: ContributingStatusState, { timeout }): void {
      state.statusRefreshTimeout = timeout
    },
    CLEAR_REFRESH_TIMEOUT(state: ContributingStatusState): void {
      state.statusRefreshTimeout = undefined
    },
    SET_SENT_STATUS(state: ContributingStatusState, status): void {
      state.sentStatus = status
    },
  },
  actions: {
    /**
     * Handle remote (realtime) contribution status.
     *
     */
    ping({ rootState }): void {
      PadletApi.ContributingStatus.ping({ token: rootState.constants.pingContributingStatusToken }) //eslint-disable-line @typescript-eslint/camelcase
    },
    updateRemote({ commit, state, rootState }, { status, user }): void {
      if (user.id === rootState.user.id) {
        return
      }
      if (status === ContributingStatus.INACTIVE) {
        const oldTimeout: number | undefined = state.statusExpireTimeout[user.id]
        if (oldTimeout) {
          clearTimeout(oldTimeout)
        }
        // set contributing status to inactive after 1 second. This is so that if it is a publish post,
        // the post can appear first before the contributing activity disappears.
        setTimeout(() => {
          commit('CLEAR_ACTIVE_STATUS', { user: user })
          commit('CLEAR_EXPIRE_TIMEOUT', { user: user })
        }, 1 * 1000)
        return
      }
      if (status === ContributingStatus.ACTIVE) {
        const oldTimeout: number | undefined = state.statusExpireTimeout[user.id]
        if (oldTimeout) {
          clearTimeout(oldTimeout)
        }
        // set contributing status to inactive after 30 seconds.
        const newTimeout = setTimeout(() => {
          commit('CLEAR_ACTIVE_STATUS', { user: user })
          commit('CLEAR_EXPIRE_TIMEOUT', { user: user })
        }, 30 * 1000)
        commit('SET_EXPIRE_TIMEOUT', { user: user, timeout: newTimeout })
        commit('SET_ACTIVE_STATUS', { user: user })
        return
      }
    },
    pushUpdate({ commit, state, rootState, rootGetters, dispatch }): void {
      // Do not push update if the page is blurred
      if (document.visibilityState === 'hidden') return
      // Send a push update with the current status
      const status = rootGetters['draft/isAnyDraftActive'] ? ContributingStatus.ACTIVE : ContributingStatus.INACTIVE
      dispatch('sendStatus', { status: status })
      if (state.statusRefreshTimeout) {
        clearTimeout(state.statusRefreshTimeout)
      }
      if (status === ContributingStatus.ACTIVE) {
        // refresh after 25 seconds
        const refreshTimeout = setTimeout(() => {
          commit('SET_SENT_STATUS', { status: ContributingStatus.INACTIVE }) // set status back to inactive to resend the status
          dispatch('pushUpdate')
        }, 25 * 1000)
        commit('SET_REFRESH_TIMEOUT', refreshTimeout)
      } else {
        commit('CLEAR_ACTIVE_STATUS', { user: rootState.user })
        commit('CLEAR_REFRESH_TIMEOUT')
      }
    },
    pushClear({ commit, state, dispatch }): void {
      // We allow clearing anytime the frontend wishes too.
      dispatch('sendStatus', { status: ContributingStatus.INACTIVE })
      if (state.statusRefreshTimeout) {
        clearTimeout(state.statusRefreshTimeout)
        commit('CLEAR_REFRESH_TIMEOUT')
      }
    },
    pushSetIfActive({ rootGetters, dispatch }): void {
      // We only allow setting if there is an active draft.
      if (rootGetters['draft/isAnyDraftActive']) {
        dispatch('sendStatus', { status: ContributingStatus.ACTIVE })
      }
    },
    sendStatus({ commit, state, rootGetters }, { status }): void {
      if (state.sentStatus !== status) {
        PadletApi.ContributingStatus.update({ wall_id: rootGetters.wallId, status }) //eslint-disable-line @typescript-eslint/camelcase
        commit('SET_SENT_STATUS', status)
      }
    },
  },
}

export default contributingStatusModule
export { ContributingStatusState, initialState }
